"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Integer = void 0;
const jsbi_1 = __importDefault(require("jsbi"));
const FromJsConstructor_1 = require("./FromJsConstructor");
const Value_1 = require("./Value");
const IonTypes_1 = require("../IonTypes");
// JSBI is an irregular class type in that it provides no constructor, only static
// constructor methods. This means that while it is a class type and `instanceof JSBI`
// works as expected, the JSBI class does not conform to the typical Constructor
// interface of new(...args) => any. Because FromJsConstructor will only use it for
// instanceof tests, we can safely cast it as a Constructor to satisfy the compiler.
const _jsbiConstructor = jsbi_1.default;
const _fromJsConstructor = new FromJsConstructor_1.FromJsConstructorBuilder()
    .withPrimitives(FromJsConstructor_1.Primitives.Number)
    .withClassesToUnbox(Number)
    .withClasses(_jsbiConstructor)
    .build();
/**
 * Represents an integer value in an Ion stream.
 *
 * [1] http://amzn.github.io/ion-docs/docs/spec.html#int
 */
class Integer extends (0, Value_1.Value)(Number, IonTypes_1.IonTypes.INT, _fromJsConstructor) {
    /**
     * Constructor.
     * @param value         The numeric value to represent as an integer.
     * @param annotations   An optional array of strings to associate with `value`.
     */
    constructor(value, annotations = []) {
        // If the provided value is a JS number, we will defer constructing a BigInt representation
        // of it until it's requested later by a call to bigIntValue().
        if (typeof value === "number") {
            super(value);
            this._numberValue = value;
            this._bigIntValue = null;
        }
        else {
            const numberValue = jsbi_1.default.toNumber(value);
            super(numberValue);
            this._bigIntValue = value;
            this._numberValue = numberValue;
        }
        this._setAnnotations(annotations);
    }
    bigIntValue() {
        if (this._bigIntValue === null) {
            this._bigIntValue = jsbi_1.default.BigInt(this.numberValue());
        }
        return this._bigIntValue;
    }
    numberValue() {
        return this._numberValue;
    }
    toString() {
        if (this._bigIntValue === null) {
            return this._numberValue.toString();
        }
        return this._bigIntValue.toString();
    }
    valueOf() {
        return this.numberValue();
    }
    writeTo(writer) {
        writer.setAnnotations(this.getAnnotations());
        if (this._bigIntValue === null) {
            writer.writeInt(this.numberValue());
        }
        else {
            writer.writeInt(this._bigIntValue);
        }
    }
    _valueEquals(other, options = {
        epsilon: null,
        ignoreAnnotations: false,
        ignoreTimestampPrecision: false,
        onlyCompareIon: true,
    }) {
        let isSupportedType = false;
        let valueToCompare = null;
        // if the provided value is an ion.dom.Integer instance.
        if (other instanceof Integer) {
            isSupportedType = true;
            if (this._bigIntValue == null && other._bigIntValue == null) {
                valueToCompare = other.numberValue();
            }
            else {
                // One of them is a JSBI
                valueToCompare = other.bigIntValue();
            }
        }
        else if (!options.onlyCompareIon) {
            // We will consider other Integer-ish types
            if (other instanceof Number || typeof other === "number") {
                isSupportedType = true;
                if (this.bigIntValue == null) {
                    valueToCompare = other.valueOf();
                }
                else {
                    valueToCompare = jsbi_1.default.BigInt(other.valueOf());
                }
            }
            else if (other instanceof jsbi_1.default) {
                isSupportedType = true;
                valueToCompare = other;
            }
        }
        if (!isSupportedType) {
            return false;
        }
        if (valueToCompare instanceof jsbi_1.default) {
            return jsbi_1.default.equal(this.bigIntValue(), valueToCompare);
        }
        return this.numberValue() == valueToCompare;
    }
}
exports.Integer = Integer;
